// Static legal pages — Customer Terms, Privacy Policy, and the B2B Merchant
// Agreement. Linked from the customer booking footer (#/terms, #/privacy) and
// the shop-owner signup gate (#/merchant).
//
// ⚖️  IMPORTANT FOR THE OPERATOR:
//   This is a strengthened, HK-oriented DRAFT — not a substitute for advice
//   from a qualified HK solicitor. Before public reliance you MUST:
//     1. Replace the OPERATOR_* placeholders below with your real registered
//        company name, Business Registration number, and support email.
//     2. Have a HK solicitor review, ESPECIALLY the liability cap, indemnity,
//        data-processor and payment clauses.
//     3. Bump EFFECTIVE_DATE + the PLATFORM_TOS_VERSION in App.jsx on any
//        material change so consent is recorded against the correct text.
//   Self-contained (no app state/auth) so it renders for any visitor.

// ── Operator identity — EDIT THESE ─────────────────────────────────────────
const OPERATOR_NAME = "PettoBook";
// e.g. "毛書科技有限公司 PettoBook Technology Limited（商業登記號碼：XXXXXXXX）"
const OPERATOR_LEGAL = "PettoBook（〔請填寫公司法定名稱及商業登記號碼〕）";
const SUPPORT_EMAIL = "support@pettobook.com"; // ← replace with your real address
const EFFECTIVE_DATE = "2026 年 7 月 6 日";

const Page = ({ title, children, onBack }) => (
  <div style={{
    minHeight: "100vh",
    background: "#FCFCFF",
    padding: "20px 16px 60px",
    fontFamily: "'Nunito', 'SF Pro Rounded', -apple-system, BlinkMacSystemFont, sans-serif",
  }}>
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <button onClick={onBack} style={{
        background: "none", border: "none", color: "#8A8A9E",
        fontSize: 14, fontWeight: 700, padding: 0, marginBottom: 16, cursor: "pointer",
        display: "inline-flex", alignItems: "center", gap: 6,
      }}>← 返回</button>
      <h1 style={{ fontSize: 28, fontWeight: 900, color: "#4A4A68", margin: "0 0 8px" }}>{title}</h1>
      <p style={{ fontSize: 12, color: "#AFAFC4", margin: "0 0 24px", fontWeight: 700 }}>
        生效日期：{EFFECTIVE_DATE}
      </p>
      <div style={{ background: "#fff", borderRadius: 20, padding: 24, boxShadow: "0 4px 16px rgba(0,0,0,0.04)", lineHeight: 1.7, color: "#4A4A68", fontSize: 14 }}>
        {children}
      </div>
      <p style={{ fontSize: 11, color: "#AFAFC4", fontWeight: 700, textAlign: "center", margin: "24px 0 0" }}>
        Powered by PettoBook
      </p>
    </div>
  </div>
);

const H = ({ children }) => <h2 style={{ fontSize: 17, fontWeight: 900, color: "#4A4A68", margin: "20px 0 8px" }}>{children}</h2>;
const P = ({ children }) => <p style={{ margin: "0 0 12px" }}>{children}</p>;
const Ul = ({ items }) => <ul style={{ margin: "0 0 12px", paddingLeft: 22 }}>{items.map((it, i) => <li key={i} style={{ marginBottom: 4 }}>{it}</li>)}</ul>;

// ════════════════════════════════════════════════════════════════════════
//  PRIVACY POLICY
// ════════════════════════════════════════════════════════════════════════
export function PrivacyPage({ onBack }) {
  return (
    <Page title="私隱政策 Privacy Policy" onBack={onBack}>
      <P>{OPERATOR_LEGAL}（「{OPERATOR_NAME}」、「我們」）尊重並保護您的私隱。本政策依據香港《個人資料（私隱）條例》（第 486 章，「PDPO」）制訂，說明我們收集、使用、儲存、轉移及保護個人資料的方式。</P>

      <H>1. 資料角色（重要）</H>
      <P>{OPERATOR_NAME} 為一個供寵物美容院（「店家」）使用的軟件平台。就客戶及毛孩資料而言：</P>
      <Ul items={[
        "您光顧之店家為該等資料之「資料使用者」（data user），負責決定收集及使用方式；",
        "{OPERATOR_NAME} 主要作為店家之「資料處理者」，按店家指示代其儲存及處理資料；",
        "就平台帳戶、登入及技術資料而言，{OPERATOR_NAME} 亦自身作為資料使用者。".replace(/\{OPERATOR_NAME\}/g, OPERATOR_NAME),
      ].map(s => s.replace(/\{OPERATOR_NAME\}/g, OPERATOR_NAME))}/>

      <H>2. 我們收集的資料</H>
      <Ul items={[
        "帳戶資料：姓名、電話、電郵地址、登入憑證",
        "毛孩資料：寵物姓名、品種、體重、毛長、過敏、健康狀況",
        "預約及交易紀錄：服務、日期、時間、美容師、費用、付款狀態",
        "通訊紀錄：WhatsApp / SMS / Email 通知及交易訊息",
        "技術資料：瀏覽器、IP 位址、裝置及登入時間（用於保安、防止濫用及偵錯）",
      ]}/>

      <H>3. 使用目的</H>
      <P>資料僅用於：(a) 處理及確認預約；(b) 為毛孩提供個人化美容紀錄及建議；(c) 發送交易性通知（確認、提醒、收據）；(d) 保安、防止濫用及系統偵錯；(e) 匿名化統計分析與服務改進；(f) 履行法律或監管要求。</P>

      <H>4. 直接促銷（Direct Marketing）</H>
      <P>我們及店家<strong>不會在未取得您明確同意前</strong>將您的個人資料用於直接促銷。任何促銷訊息均設有退出（opt-out）方法；您可隨時於通知設定或回覆訊息中選擇停止接收，我們會於合理時間內處理。我們<strong>不會出售</strong>您的個人資料予第三方作促銷用途。</P>

      <H>5. 資料分享</H>
      <P>除以下情況外，我們不會向第三方披露個人資料：</P>
      <Ul items={[
        "您所選定之店家店主及員工（為履行預約服務所需）",
        "支付服務供應商（如 Stripe）以處理付款",
        "通訊服務供應商（如 Meta WhatsApp / Twilio / Resend）以發送通知",
        "雲端託管供應商（如 Supabase / Vercel）以儲存及運行服務",
        "法律、法規或司法命令要求之披露",
      ]}/>

      <H>6. 跨境資料轉移</H>
      <P>我們的雲端基礎設施可能位於香港以外地區（例如新加坡等亞太區）。使用本平台即表示您理解並同意個人資料可能被轉移至香港境外並在當地儲存及處理。我們會採取合理措施確保受託處理者提供不低於本政策之保障水平。</P>

      <H>7. 資料儲存及保安</H>
      <P>資料儲存於符合行業安全標準之雲端伺服器。我們採用 HTTPS 加密傳輸、資料列級安全（Row Level Security）隔離各店家資料、定期備份及最少權限存取原則。惟無任何系統可保證絕對安全。如發生影響您個人資料之重大保安事故，我們會在切實可行範圍內從速通知受影響用戶及／或有關店家。</P>

      <H>8. 資料保留期</H>
      <P>帳戶資料於帳戶刪除後保留最多 90 天，之後刪除或匿名化；惟預約、發票及財務紀錄按香港法律要求保留最少 7 年（此保留要求優先於前述 90 天）。</P>

      <H>9. 您的權利（PDPO）</H>
      <P>您有權查閱、複本及更正您的個人資料，並可要求刪除帳戶（受法律保留要求限制）或撤回促銷同意。我們會於收到<strong>符合規定之資料查閱要求後 40 天內</strong>回覆。行使權利請電郵至 <strong>{SUPPORT_EMAIL}</strong>，或聯絡您光顧之店家。</P>

      <H>10. Cookies 及本地儲存</H>
      <P>本應用使用瀏覽器本地儲存（localStorage / sessionStorage）保存登入狀態、語言偏好及預約過程中之暫存資料。我們不使用第三方廣告 cookie。</P>

      <H>11. 兒童私隱</H>
      <P>本平台不面向 13 歲以下兒童提供服務。如我們發現在未經家長同意下收集了此類資料，將予以刪除。</P>

      <H>12. 政策修訂</H>
      <P>本政策可能不時更新。重大修訂會透過應用內通知或於本頁公佈。繼續使用即視為接受修訂後之政策。</P>

      <H>13. 聯絡我們</H>
      <P>有關私隱之查詢或投訴，請電郵至 <strong>{SUPPORT_EMAIL}</strong>。</P>
    </Page>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  CUSTOMER TERMS OF SERVICE
// ════════════════════════════════════════════════════════════════════════
export function TermsPage({ onBack }) {
  return (
    <Page title="服務條款 Terms of Service" onBack={onBack}>
      <P>歡迎使用 {OPERATOR_NAME}。本條款為您（客戶）與 {OPERATOR_LEGAL}（「{OPERATOR_NAME}」）之間就使用本平台之協議。使用本平台即表示您已閱讀、理解並同意以下條款。店家與 {OPERATOR_NAME} 之間之關係另受《商戶服務協議》規管。</P>

      <H>1. 服務性質</H>
      <P>{OPERATOR_NAME} 為一個美容院管理及預約平台，連接客戶與獨立經營之寵物美容院（「店家」）。實際美容服務由店家提供。{OPERATOR_NAME} 並非美容服務之提供者、代理人或僱主，亦不就店家所提供服務之品質、安全性、合法性或結果作任何保證或承擔責任。</P>

      <H>2. 帳戶</H>
      <Ul items={[
        "您須提供準確、完整及最新之資料（姓名、電話、毛孩資料）；",
        "您須妥善保管登入憑證，並對帳戶下所有活動負責；",
        "禁止冒用他人身份、提供虛假資料或創建多重帳戶以濫用優惠或系統；",
        `${OPERATOR_NAME} 保留因違反本條款而暫停或終止帳戶之權利。`,
      ]}/>

      <H>3. 預約、訂金及取消</H>
      <P>預約須符合店家所訂之條件（包括訂金及取消政策），該等政策由各店家自行設定並於預約時顯示。</P>
      <Ul items={[
        "作出預約即表示接受該店家當時之取消及退款政策；",
        "逾期取消可能被扣除部分或全部訂金；",
        "未到店（No-Show）按店家政策收取費用；",
        "店家保留拒絕為具攻擊性、患病或不符接待條件之毛孩服務之權利。",
      ]}/>

      <H>4. 付款</H>
      <P>付款金額包括服務費、可選附加項目、稅項（如適用）及任何於現場新增之附加費用。支付方式包括線上付款（如 Stripe）、儲值錢包或現場現金／電子付款。線上付款由第三方支付機構處理；{OPERATOR_NAME} 並非支付機構，付款爭議或退款須由您與店家（及／或支付機構）處理。</P>

      <H>5. 用戶生成內容</H>
      <P>您上傳之毛孩照片、評價及備註內容之擁有權仍屬您所有。您授予店家及 {OPERATOR_NAME} 一項非專屬、免版稅之全球授權，為提供、營運及改進服務而使用該等內容；用於公開行銷推廣則僅限經去識別化或另經您同意之情況。您保證擁有上傳該等內容之權利，且內容不侵犯第三方權利或違反法律。</P>

      <H>6. 可接受使用</H>
      <P>您不得：(a) 違法使用本平台；(b) 上傳虛假、侵權、惡意或違法內容；(c) 干擾或試圖破壞平台安全；(d) 濫用預約或通訊功能（例如惡意大量預約或訊息滋擾）。違者我們可即時暫停帳戶。</P>

      <H>7. 知識產權</H>
      <P>本平台之介面、商標、標誌及程式碼之知識產權歸 {OPERATOR_NAME} 所有。店家之品牌資料屬各店家所有。未經授權不得複製、修改或作商業利用。</P>

      <H>8. 免責聲明</H>
      <P>本平台按「現狀」及「現有」基礎提供。於法律允許之最大範圍內，{OPERATOR_NAME} 不就服務無中斷、無錯誤、及格供銷或符合特定用途作任何明示或默示保證。</P>

      <H>9. 責任限制</H>
      <P>於法律允許之最大範圍內，{OPERATOR_NAME} 對任何因使用（或無法使用）本平台、店家提供之服務、或第三方供應商之作為或失誤所引致之間接、附帶、特殊或衍生損害概不負責。{OPERATOR_NAME} 就任何與本平台有關之申索之總責任，不論基於合約、侵權或其他，均以下列較高者為上限：(a) 您於申索發生前 12 個月內就使用本平台直接支付予 {OPERATOR_NAME} 之款項；或 (b) 港幣 500 元。</P>
      <P><strong>惟本條款概不排除或限制任何依香港法律（包括《管制免責條款條例》第 71 章）不可合法排除或限制之責任，包括因疏忽導致之人身傷亡之責任。</strong></P>

      <H>10. 彌償</H>
      <P>您同意就因您違反本條款、您上傳之內容、或您濫用本平台而引致或有關之任何第三方申索、損失、責任及合理費用（包括律師費），對 {OPERATOR_NAME} 作出彌償並使其免受損害。</P>

      <H>11. 關係</H>
      <P>本條款不構成 {OPERATOR_NAME} 與您或任何店家之間之合夥、合資、代理或僱傭關係。店家為獨立營運者。</P>

      <H>12. 不可抗力</H>
      <P>對於因超出合理控制範圍之事件（包括天災、疫症、停電、電訊或第三方服務中斷、政府命令等）所致之延誤或未能履行，{OPERATOR_NAME} 不承擔責任。</P>

      <H>13. 管轄法律及爭議</H>
      <P>本條款受香港特別行政區法律管轄。任何爭議受香港法院之非排他性管轄。</P>

      <H>14. 一般條款</H>
      <Ul items={[
        "可分割性：任何條款如被裁定無效，其餘條款仍具效力；",
        "完整協議：本條款連同私隱政策構成雙方就標的之完整協議；",
        "棄權：任何一方未行使權利不構成放棄該權利；",
        "修訂：{OPERATOR_NAME} 可不時修訂本條款，重大修訂會於應用內通知，繼續使用即視為接受。".replace(/\{OPERATOR_NAME\}/g, OPERATOR_NAME),
      ].map(s => s.replace(/\{OPERATOR_NAME\}/g, OPERATOR_NAME))}/>

      <H>15. 聯絡我們</H>
      <P>條款查詢請聯絡您光顧之店家，或電郵至 <strong>{SUPPORT_EMAIL}</strong>。</P>
    </Page>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  MERCHANT (SHOP) AGREEMENT — B2B SaaS
// ════════════════════════════════════════════════════════════════════════
export function MerchantPage({ onBack }) {
  return (
    <Page title="商戶服務協議 Merchant Agreement" onBack={onBack}>
      <P>本《商戶服務協議》（「本協議」）為 {OPERATOR_LEGAL}（「{OPERATOR_NAME}」、「我們」）與使用本平台經營寵物美容業務之商戶（「店家」、「您」）之間之協議。在開設店鋪或使用本平台之商戶功能前，請仔細閱讀。點選同意或使用本平台即表示您接受本協議，並確認您<strong>有權代表該店家締結本協議</strong>。</P>

      <H>1. 服務授予</H>
      <P>在您遵守本協議之前提下，{OPERATOR_NAME} 授予您一項非專屬、不可轉讓、可撤回之權利，於訂閱期內使用本平台管理您的預約、客戶、員工及相關業務。</P>

      <H>2. 帳戶與授權</H>
      <Ul items={[
        "您保證所提供之店鋪及帳戶資料真實、準確及最新；",
        "簽署人保證已獲店家充分授權締結本協議並承擔其義務；",
        "您須對帳戶下所有活動及員工使用負責，並妥善管理存取權限。",
      ]}/>

      <H>3. 訂閱、收費及付款</H>
      <Ul items={[
        "訂閱方案及費用以下單時平台所顯示者為準；費用可能不含適用稅項；",
        "除另有訂明外，訂閱按月／按週期預繳並自動續期，直至您取消；",
        "我們可調整收費，並會於生效前作出合理事先通知；您如不接受，可於生效前取消；",
        "除法律要求或我們另行書面同意外，已付費用一般不予退還，取消於當前計費週期結束時生效；",
        "逾期或未能付款者，我們可暫停或限制您使用付費功能，直至清繳。",
      ]}/>

      <H>4. 免費試用</H>
      <P>如提供免費試用或免費方案，我們可隨時更改或終止其條件。試用期內產生之資料在您升級後保留；如試用結束未升級，部分付費功能將停用。</P>

      <H>5. 店家責任與保證</H>
      <Ul items={[
        "合法經營：您保證已取得經營寵物美容業務所需之一切牌照、許可及保險，並遵守所有適用法律；",
        "資料合規：您保證已就上載至平台之每位客戶及毛孩資料取得必要之同意及合法依據；",
        "服務履行：您全權負責向客戶提供之美容服務、其品質、安全及後果；",
        "通訊合規：您使用平台之通知／SMS／WhatsApp／Email 功能時須遵守反滋擾及直接促銷法規，不得發送未經同意之促銷；",
        "帳目準確：您負責維持店內服務、價格、員工及財務資料之準確性。",
      ]}/>

      <H>6. 個人資料保護（資料處理）</H>
      <P>就您的客戶及毛孩之個人資料：<strong>您為資料使用者（data user），{OPERATOR_NAME} 為代您處理資料之資料處理者。</strong></P>
      <Ul items={[
        "我們僅按您之指示及為提供服務之目的處理該等資料；",
        "我們會採取合理技術及組織措施保障資料安全（見私隱政策）；",
        "您保證對客戶資料之收集及使用符合 PDPO 及其他適用法律，並已向客戶作出所需通知及取得同意；",
        "如發生影響資料之保安事故，我們會在切實可行範圍內從速通知您，以便您履行法定通報責任；",
        "協議終止後，我們會按第 11 條處理資料之匯出及刪除。",
      ]}/>

      <H>7. 付款處理</H>
      <P>如您透過第三方支付機構（如 Stripe）收款，該等付款由相關支付機構按其條款處理。{OPERATOR_NAME} 並非支付機構、資金保管人或收款代理，不對付款、退款、拒付（chargeback）或支付機構之作為負責。客戶與您之間之交易爭議由您自行處理。</P>

      <H>8. 知識產權</H>
      <P>本平台之全部知識產權（軟件、介面、商標、程式碼）歸 {OPERATOR_NAME} 所有。您的店鋪品牌、標誌及內容歸您所有；您授予 {OPERATOR_NAME} 為運行及推廣平台服務所需之有限授權以使用該等內容。</P>

      <H>9. 保密</H>
      <P>任何一方就本協議所獲之另一方非公開資料須保密，且僅為履行本協議而使用，惟法律要求披露者除外。</P>

      <H>10. 服務可用性</H>
      <P>本平台按「現狀」提供。除另有書面服務水平協議外，{OPERATOR_NAME} 不保證服務無中斷或無錯誤，並可為維護、更新或保安而暫時中斷服務，我們會在合理可行情況下事先通知。</P>

      <H>11. 暫停與終止</H>
      <Ul items={[
        "任何一方可按方案條款終止訂閱；您可於帳戶內取消，於當前週期結束時生效；",
        "如您重大違反本協議（包括未付款、違法使用或危害平台安全），我們可暫停或即時終止您的帳戶；",
        "終止後，您可於 30 天內匯出您的資料；其後我們可依私隱政策及法律保留要求刪除或匿名化資料（財務紀錄按法定期限保留）；",
        "終止不影響任何一方於終止前已產生之權利及責任。",
      ]}/>

      <H>12. 免責聲明</H>
      <P>於法律允許之最大範圍內，{OPERATOR_NAME} 不就本平台作任何明示或默示保證，包括就及格供銷、符合特定用途或不侵權之默示保證。</P>

      <H>13. 責任限制</H>
      <P>於法律允許之最大範圍內，{OPERATOR_NAME} 對任何間接、附帶、特殊、懲罰性或衍生損害（包括利潤損失、業務中斷、商譽或資料損失）概不負責。{OPERATOR_NAME} 就本協議項下所有申索之總責任，不論基於合約、侵權或其他，均以您於引致申索之事件發生前 12 個月內就本平台實際支付予 {OPERATOR_NAME} 之費用為上限。</P>
      <P><strong>本條款概不排除或限制任何依香港法律（包括《管制免責條款條例》第 71 章）不可合法排除或限制之責任，包括因疏忽導致之人身傷亡之責任。</strong></P>

      <H>14. 彌償（重要）</H>
      <P>您同意就以下事項所引致或有關之任何第三方申索、法律程序、損失、損害、罰款及合理費用（包括律師費），對 {OPERATOR_NAME} 及其董事、僱員及代理人作出彌償、抗辯並使其免受損害：(a) 您向客戶提供之服務；(b) 您違反本協議或任何法律；(c) 您上載或處理之資料（包括未取得客戶所需同意）；(d) 您與客戶、員工或第三方之爭議。</P>

      <H>15. 關係</H>
      <P>雙方為獨立訂約方。本協議不構成合夥、合資、代理或僱傭關係，任何一方均無權代另一方作出承諾。</P>

      <H>16. 不可抗力</H>
      <P>對於因超出合理控制範圍之事件所致之延誤或未能履行（付款義務除外），任何一方均不承擔責任。</P>

      <H>17. 修訂</H>
      <P>{OPERATOR_NAME} 可不時修訂本協議，並會於生效前作出合理通知。於通知後繼續使用本平台即視為接受修訂後之協議。</P>

      <H>18. 一般條款</H>
      <Ul items={[
        "管轄法律：本協議受香港特別行政區法律管轄，並受香港法院之非排他性管轄；",
        "轉讓：未經 {OPERATOR_NAME} 事先書面同意，您不得轉讓本協議；{OPERATOR_NAME} 可於業務轉讓時轉讓本協議；".replace(/\{OPERATOR_NAME\}/g, OPERATOR_NAME),
        "可分割性：任何條款無效不影響其餘條款；",
        "完整協議：本協議連同適用之訂閱條款構成雙方之完整協議；",
        "棄權：未行使任何權利不構成放棄。",
      ].map(s => s.replace(/\{OPERATOR_NAME\}/g, OPERATOR_NAME))}/>

      <H>19. 聯絡我們</H>
      <P>有關本協議之查詢，請電郵至 <strong>{SUPPORT_EMAIL}</strong>。</P>
    </Page>
  );
}
